import { useState } from 'react';

import { useNavigate } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { usePlanLocations } from '@/hooks/usePlanLocations';
import usePlanOperators from '@/hooks/usePlanOperators';
import usePlanOwner from '@/hooks/usePlanOwner';

import { itemByLanguage } from '@/utils/language';

import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import { routes } from '@/constants/routes';

import { EnableCompliancePlanModal } from './EnableCompliancePlanModal';

import {
  getManagedPlansOptions,
  getPlanAndAssignmentsOptions,
  getUsersForLocations,
  Plan,
  type ReadonlySubscriptionRequest,
  upsertReadonlySubscriptionMutation,
} from '@/_generated/api-clients/xp';
import { useAssignTeamMembers } from '@/routes/TrainingPlans/hooks/useAssignTeamMembers';

type PlanIdentifier = Pick<Plan, 'id' | 'name'>;
interface UseAddReadOnlyPlanOptions {
  /** Called when user cancels the compliance confirmation modal */
  onCancel?: () => void;
}

interface UseAddReadOnlyPlanReturn {
  /** Subscribe to a read-only plan. Shows modal for compliance, executes directly otherwise. */
  subscribeReadonlyPlan: (plan: Plan) => void;
  unsubscribeReadonlyPlan: (plan: PlanIdentifier) => Promise<void>;
  isPending: boolean;
  pendingPlanId: string | undefined;
  /** Render this where you want the modal to appear */
  complianceModal: React.ReactNode;
}

/**
 * useAddReadOnlyPlan - Hook to manage read-only plan subscriptions.
 * Shows compliance modal for compliance plans, handles direct mutation otherwise.
 */
export const useAddReadOnlyPlan = (
  options: UseAddReadOnlyPlanOptions = {},
): UseAddReadOnlyPlanReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getLocationsByPlan = usePlanLocations();
  const { shouldSelectOwner, isMultiOwner, resolvePlanOwner } = usePlanOwner();
  const { operators, isFetching: isFetchingOperators } = usePlanOperators();
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const { autoAssignPlansEpic } = useFlags();
  const { assignTeamMembers, isPending: isAssignPending } =
    useAssignTeamMembers();

  const requirePlanId = (source: Pick<Plan, 'id'>, context: string): string => {
    if (!source.id) {
      throw new Error(`Missing plan id in ${context}`);
    }
    return source.id;
  };

  const {
    mutateAsync: upsertReadonlySubscriptionAsync,
    isPending,
    variables,
  } = useMutation({
    ...upsertReadonlySubscriptionMutation(),
    onError: error => {
      console.log('Error upserting readonly subscription', error);
      datadogRum.addError(error);
    },
  });

  const upsertForAllOwners = (
    planId: string,
    body: Omit<ReadonlySubscriptionRequest, 'ownerId' | 'locationNumber'>,
  ) => {
    const resolvedOwner = resolvePlanOwner();
    if (!resolvedOwner.ownerId && !resolvedOwner.locationNumber) {
      throw new Error(
        'Owner could not be resolved: authentication may not have loaded yet.',
      );
    }

    const allOwners: Pick<
      ReadonlySubscriptionRequest,
      'ownerId' | 'locationNumber'
    >[] = [resolvedOwner, ...operators.map(op => ({ ownerId: op.id }))];

    const seen = new Set<string>();
    const uniqueOwners = allOwners.filter(owner => {
      const key = owner.ownerId ?? owner.locationNumber ?? '';
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    return uniqueOwners.map(owner =>
      upsertReadonlySubscriptionAsync({
        path: { planId },
        body: { ...body, ...owner },
      }),
    );
  };

  const subscribeReadonlyPlan = async (plan: Plan): Promise<void> => {
    const isCompliance = plan.category === 'compliance' && plan.readonly;

    // Compliance plans show modal first
    if (isCompliance) {
      setSelectedPlan(plan);
      return;
    }

    try {
      const planId = requirePlanId(plan, 'subscribeReadonlyPlan');
      await Promise.all(upsertForAllOwners(planId, { optedIn: true }));
    } catch (e) {
      console.log('Error subscribing to readonly plan', e);
    }

    await queryClient.refetchQueries({
      queryKey: getManagedPlansOptions({ query: { status: 'ACTIVE' } })
        .queryKey,
    });

    const isAnchor = plan.type === 'ANCHOR' && plan.readonly;
    const planName = itemByLanguage(plan.name);

    const toastMessage = isAnchor
      ? shouldSelectOwner
        ? t('Settings.anchorPlanToast.successMessageMultipleOperators', {
            planName,
          })
        : t('Settings.anchorPlanToast.successMessage', { planName })
      : t('Settings.enableCompliancePlanModal.successToast', {
          planName: itemByLanguage(plan.name),
          forOperators: isMultiOwner
            ? t('Settings.enableCompliancePlanModal.successToastForOperators')
            : '',
        });

    toast.custom(toastObj => (
      <ToastMessageBlock severity="success" id={toastObj.id}>
        {toastMessage}
      </ToastMessageBlock>
    ));

    const planId = requirePlanId(plan, 'subscribeReadonlyPlan navigate');

    if (isAnchor) {
      navigate(routes.plans.managePlansEdit({ planId }));
    } else {
      navigate(routes.plans.managePlan({ planId }));
    }
  };

  const unsubscribeReadonlyPlan = async (
    plan: PlanIdentifier,
  ): Promise<void> => {
    await Promise.all(upsertForAllOwners(plan.id, { optedIn: false }));

    await queryClient.refetchQueries({
      queryKey: getManagedPlansOptions({ query: { status: 'ACTIVE' } })
        .queryKey,
    });

    toast.custom(toastObj => (
      <ToastMessageBlock severity="success" id={toastObj.id}>
        {t('TrainingPlans.planRemovedToast', {
          planName: itemByLanguage(plan.name),
        })}
      </ToastMessageBlock>
    ));
  };

  const handleModalClose = () => {
    setSelectedPlan(null);
    options.onCancel?.();
  };

  /**
   * Fetches all team members for plan locations, then delegates to
   * `assignTeamMembers` to assign all users to the plan.
   */
  const assignAllTeamMembers = async (
    plan: Plan,
    dueDateOffsetInDays?: number,
  ): Promise<void> => {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + (dueDateOffsetInDays ?? 30));
    const dueDateStr = dueDate.toISOString().split('T')[0] ?? '';
    const locations = getLocationsByPlan(plan);
    const planId = requirePlanId(plan, 'assignAllTeamMembers');

    if (locations.length === 0) {
      datadogRum.addError(
        new Error(
          `No locations available for compliance plan assignment: ${planId}`,
        ),
      );
      return;
    }

    const usersResponse = await getUsersForLocations({
      query: { locationNumbers: locations },
    });

    const allUserIds = (usersResponse.data ?? [])
      .map(u => u.adId)
      .filter(Boolean) as string[];

    if (allUserIds.length === 0) return;

    await assignTeamMembers({
      planId,
      userIds: allUserIds,
      dueDate: dueDateStr,
    });
  };

  /**
   * Confirms enabling a compliance plan.
   *
   * When `autoAssignPlansEpic` is enabled:
   * 1. Passes `autoAssign: true` to backend for new employee auto-assignment
   * 2. Assigns plan to all current team members immediately
   * 3. Resets progress for in-progress team members
   * 4. Updates due dates for already-assigned team members (reassignments)
   *
   * @remarks
   * - Frontend handles immediate bulk assignment to CURRENT team members (bullet 1)
   * - Frontend handles progress reset and due date updates for reassignments
   * - Backend handles auto-assignment for NEW employees on their start date (bullet 2)
   *
   * @assumption Backend handles recertification reassignment after plan completion.
   * When a team member completes the plan, the backend is responsible for
   * automatically reassigning the plan after the expiration period
   * (e.g., 1 year or 3 years based on `expireAfterCompletionOffsetInDays`).
   * This enables the recertification cycle described in bullet 3 of the modal.
   * - Frontend handles due date updates for reassignments
   */
  const handleModalConfirm = async () => {
    if (!selectedPlan) return;

    // Intentionally do NOT call subscribeReadonlyPlan so its
    // navigate/toast don't fire — we handle both directly here after all work is done.
    setIsConfirming(true);

    try {
      const modalPlanId = requirePlanId(selectedPlan, 'handleModalConfirm');
      const subscriptionPromises = upsertForAllOwners(modalPlanId, {
        optedIn: true,
      });

      const results = await Promise.allSettled(subscriptionPromises);

      const failedResults = results.filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );

      failedResults.forEach(result => {
        datadogRum.addError(
          result.reason instanceof Error
            ? result.reason
            : new Error(String(result.reason)),
          { section: 'readonlySubscription', planId: selectedPlan.id },
        );
      });

      if (failedResults.length > 0 && failedResults.length === results.length) {
        // All subscription attempts failed; fall back to generic error handling.
        throw new Error('All readonly subscription requests failed');
      }

      await queryClient.refetchQueries({
        queryKey: getManagedPlansOptions({ query: { status: 'ACTIVE' } })
          .queryKey,
      });

      if (autoAssignPlansEpic) {
        await assignAllTeamMembers(selectedPlan);
        queryClient.removeQueries({
          queryKey: getPlanAndAssignmentsOptions({
            path: { planId: modalPlanId },
          }).queryKey,
        });
      }

      const planName = itemByLanguage(selectedPlan.name);
      navigate(routes.plans.managePlan({ planId: modalPlanId }));
      toast.custom(toastObj => (
        <ToastMessageBlock severity="success" id={toastObj.id}>
          {t('Settings.enableCompliancePlanModal.successToast', {
            planName,
            forOperators: isMultiOwner
              ? t('Settings.enableCompliancePlanModal.successToastForOperators')
              : '',
          })}
        </ToastMessageBlock>
      ));
    } catch (e) {
      console.log('Error subscribing to readonly plan', e);
      setSelectedPlan(null);
    } finally {
      setIsConfirming(false);
    }
  };

  const complianceModal = selectedPlan ? (
    <EnableCompliancePlanModal
      plan={selectedPlan}
      isPending={
        isPending || isConfirming || isAssignPending || isFetchingOperators
      }
      onClose={handleModalClose}
      onConfirm={handleModalConfirm}
    />
  ) : null;

  return {
    subscribeReadonlyPlan,
    unsubscribeReadonlyPlan,
    isPending,
    pendingPlanId: variables?.path.planId,
    complianceModal,
  };
};
